import { useMemo, useState } from 'react';
import { QrCodeIcon } from '@phosphor-icons/react';
import { cn } from '@/utils/utils';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/cms/ui/dialog';
import { Panel, PanelBody, PanelContent, PanelTrigger } from '@/components/ui/panel';
import { Overline } from '@/components/ui/text';
import { Tile } from '@/components/ui/tile';
import { CompactSharePanel } from './SharePanel';
import { TAKEN_DOWN_NOTE } from '@/hooks/useShareLink';
import { StoryGlyph, VERSION_BOX, VERSION_TEXT } from './steps/SelectScenesStep';
import { VERSION_STATUS_LABEL } from './versionOptions';
import type { ProjectSceneTarget, PublishVersionOption } from './types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The publish flow's list: each story's order, cover and versions. */
  scenes: ProjectSceneTarget[];
}

// The version a story is shared by, and the group it sits in: live, else in review, else the newest draft
const SHARED_STATUSES = ['live', 'pending', 'draft'] as const;
type SharedStatus = (typeof SHARED_STATUSES)[number];

const GROUP_LABEL: Record<SharedStatus | 'other', string> = {
  live: 'Live',
  pending: 'In review',
  draft: 'Draft',
  other: 'Nothing to share',
};

interface Entry {
  scene: ProjectSceneTarget;
  order: number;
  version: PublishVersionOption | null;
}

function sharedVersion(scene: ProjectSceneTarget): PublishVersionOption | null {
  for (const status of SHARED_STATUSES) {
    // versions arrive newest first
    const match = scene.versions.find((v) => v.status === status);
    if (match) return match;
  }
  return null;
}

function versionText(version: PublishVersionOption | null, scene: ProjectSceneTarget): string {
  const shown = version ?? scene.versions[0];
  if (!shown) return 'No versions';
  const label = VERSION_STATUS_LABEL[shown.status];
  return shown.versionNumber != null ? `v${shown.versionNumber} · ${label}` : label;
}

/**
 * A project has no link of its own: links are per story (share links 3.2), and the old project deep
 * link needed a sign-in for unpublished projects. So the project's share dialog lists its stories in
 * the publish flow's story rows, grouped by status, and a row opens that story's link in a card the
 * width of the row's version half, over the list rather than inside it, so nothing below moves.
 */
export function ProjectShareDialog({ open, onOpenChange, scenes }: Props) {
  const [openId, setOpenId] = useState<string | null>(null);

  const groups = useMemo(() => {
    const ordered = [...scenes].sort((a, b) => a.orderNumber - b.orderNumber);
    const entries: Entry[] = ordered.map((scene, i) => ({ scene, order: i + 1, version: sharedVersion(scene) }));
    return (['live', 'pending', 'draft', 'other'] as const)
      .map((key) => ({
        key,
        entries: entries.filter((e) => (e.version ? e.version.status === key : key === 'other')),
      }))
      .filter((g) => g.entries.length > 0);
  }, [scenes]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* The publish flow's dialog: the surface over a light, blurred scrim */}
      <DialogContent width="lg" scrim="soft" className="max-w-184 bg-lureo-surface">
        <DialogHeader>
          <DialogTitle className="text-lureo-16">Share / Test your project</DialogTitle>
        </DialogHeader>

        <DialogBody className="gap-5">
          <DialogDescription className="font-lureo-font-prose text-lureo-13 leading-[1.4]">
            Each story has its own link. Open one to see its QR code.
          </DialogDescription>

          {groups.length === 0 ? (
            <p className="font-lureo-font-prose text-lureo-14 text-lureo-ink-subtle">This project has no stories yet.</p>
          ) : (
            groups.map((group) => (
              <section key={group.key} className="flex flex-col gap-2">
                <Overline size="label" as="h4">{GROUP_LABEL[group.key]}</Overline>
                <ol className="space-y-2">
                  {group.entries.map(({ scene, order, version }) => (
                    <StoryLinkRow
                      key={scene.sceneOriginId}
                      scene={scene}
                      order={order}
                      version={version}
                      isOpen={openId === scene.sceneOriginId}
                      onOpenChange={(next) => setOpenId(next ? scene.sceneOriginId : null)}
                    />
                  ))}
                </ol>
              </section>
            ))
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

interface RowProps {
  scene: ProjectSceneTarget;
  order: number;
  version: PublishVersionOption | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

// The publish flow's story row: the story in its first half, the version it's shared by in its second, a QR mark where the publish list's check sits
function StoryLinkRow({ scene, order, version, isOpen, onOpenChange }: RowProps) {
  const shareable = version != null;
  const takenDown = scene.visibility === 'takenDown';

  return (
    <Panel open={isOpen} onOpenChange={onOpenChange}>
      <li>
        <PanelTrigger asChild disabled={!shareable}>
          <Tile
            layout="split"
            selected={isOpen}
            // It opens a panel, which aria-expanded announces
            aria-pressed={undefined}
            aria-label={shareable ? `Show the link for ${scene.sceneOriginName || 'this story'}` : undefined}
            glyph={<StoryGlyph order={order} coverImageUrl={scene.coverImageUrl} highlighted={shareable} />}
            label={scene.sceneOriginName || 'Untitled story'}
            trailing={
              <>
                <span className="flex min-w-0 flex-col gap-1.5">
                  <Overline size="label" as="span">
                    Version
                  </Overline>
                  <span className={cn(VERSION_BOX, VERSION_TEXT, 'border-transparent', shareable ? 'text-lureo-ink-subtle' : 'text-lureo-ink-subtlest')}>
                    {takenDown ? 'Taken down' : versionText(version, scene)}
                  </span>
                </span>
                <span
                  aria-hidden
                  className={cn(
                    'ml-auto flex h-6 w-6 shrink-0 items-center justify-center rounded-lureo-radius border-lureo-border-width-medium transition-colors',
                    !shareable
                      ? 'border-lureo-line bg-lureo-neutral text-lureo-ink-subtlest'
                      : isOpen
                        ? 'border-lureo-brand-ink bg-lureo-brand text-lureo-on-brand'
                        : 'border-lureo-line-strong bg-lureo-surface text-lureo-brand-ink',
                  )}
                >
                  <QrCodeIcon weight="bold" className="h-3.5 w-3.5" />
                </span>
              </>
            }
          />
        </PanelTrigger>
      </li>

      {shareable && (
        // The width of the row's version half, at its end
        <PanelContent align="end" onOpenAutoFocus={(e) => e.preventDefault()} className="w-[calc(var(--radix-popover-trigger-width)/2)]">
          <PanelBody>
            {takenDown ? (
              <p className="font-lureo-font-prose text-lureo-13 leading-[1.4] text-lureo-ink-subtle">{TAKEN_DOWN_NOTE}</p>
            ) : (
              <CompactSharePanel sceneInstanceId={version.sceneInstanceId} />
            )}
          </PanelBody>
        </PanelContent>
      )}
    </Panel>
  );
}
